/**
 * Unified API Client Export
 *
 * This file provides automatic environment detection to select the correct API client:
 * - Server-side (SSG/SSR/Server Components) → Uses serverFetch (direct API calls with Bearer token)
 * - Client-side (Browser) → Uses browserFetch (direct API calls with cross-subdomain cookies)
 *
 * @example
 * ```tsx
 * import { ApiClient } from '@website/lib/api';
 *
 * // Works in both server and client contexts
 * const data = await ApiClient.getActivities({ limit: 10 });
 * ```
 */
import { type CustomFetchFn, createApiClient } from '@api';

import { browserFetch } from './browserFetch';

/**
 * Determines if code is running on the server side.
 * During SSG/SSR build, this returns true.
 * In the browser, this returns false.
 */
const isServer = typeof window === 'undefined';

/**
 * Get the appropriate fetch implementation based on environment.
 * Uses dynamic require for serverFetch to avoid loading server-only modules in the browser.
 */
const getCustomFetch = (): CustomFetchFn => {
  if (isServer) {
    // Dynamic require to avoid loading server-only modules (apiConcurrencyLimiter, logger)
    // in the browser bundle. This is evaluated at runtime, not build time.
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    return require('./serverFetch').serverFetch;
  }
  return browserFetch;
};

/**
 * Unified API client that automatically selects the correct fetch implementation
 * based on the execution environment.
 *
 * - On server: Uses direct API URL with Bearer token authentication
 * - On client: Uses direct API URL with cross-subdomain cookies (credentials: 'include')
 */
export const ApiClient = createApiClient(getCustomFetch());

// Re-export types
export type { ApiClient as ApiClientType, CustomFetchFn, CustomFetchOptions } from '@api';
